import { c as _c } from "react/compiler-runtime";
import { IcoStore16, IcoUser16 } from '@onefootprint/icons';
import { Badge } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
type CheckType = 'pep' | 'sanctions' | 'adverse_media' | 'watchlist';
type CheckStatus = 'clear' | 'hit' | 'unknown';
type ScreeningCheck = {
  type: CheckType;
  status: CheckStatus;
  detail?: string;
  source?: string;
};
type ScreeningSubject = {
  name: string;
  kind?: 'person' | 'business';
  role?: string;
  metadata?: string;
  checks: ScreeningCheck[];
};
export type ScreeningResultsProps = {
  title?: string;
  subjects: ScreeningSubject[];
};
const CHECK_LABEL: Record<CheckType, string> = {
  pep: 'PEP',
  sanctions: 'Sanctions',
  adverse_media: 'Adverse media',
  watchlist: 'Watchlist'
};

// Written out rather than built from the status, because Tailwind reads the source as text: a
// `text-${status}` it never sees is a class it never generates.
const STATUS_COLOR: Record<CheckStatus, string> = {
  hit: 'text-error',
  unknown: 'text-warning',
  clear: 'text-success'
};
const subjectBadge = (checks: ScreeningCheck[]): {
  label: string;
  variant: 'error' | 'warning' | 'success';
} => {
  const hitCheck = checks.find(c => c.status === 'hit');
  if (hitCheck) return {
    label: CHECK_LABEL[hitCheck.type],
    variant: 'error'
  };
  if (checks.some(c => c.status === 'unknown')) return {
    label: 'Unknown',
    variant: 'warning'
  };
  return {
    label: 'Clear',
    variant: 'success'
  };
};
const ScreeningResults = t0 => {
  const $ = _c(9);
  const {
    title,
    subjects: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const subjects = t2;
  let t3;
  if ($[2] !== title) {
    t3 = title && <span className="text-label-2 text-primary fp-text">{title}</span>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== subjects) {
    t4 = subjects.map(_temp4);
    $[4] = subjects;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="flex flex-col gap-3 w-full">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export default ScreeningResults;
function _temp(check) {
  return <div key={check.type} className="flex items-start gap-4 min-h-6"><p className="text-body-2 text-tertiary fp-text w-24 flex-none whitespace-nowrap">{CHECK_LABEL[check.type]}</p><p className={cx("text-body-2 fp-text flex-1 min-w-0 break-words text-right", STATUS_COLOR[check.status])}>{check.detail || (check.status === "hit" ? "Hit" : check.status === "unknown" ? "Unknown" : "No match")}</p></div>;
}
function _temp2(c) {
  return c.source;
}
function _temp3(check_0) {
  return <div key={`${check_0.type}-src`} className="flex items-start gap-4 min-h-6"><p className="text-body-2 text-tertiary fp-text w-24 flex-none whitespace-nowrap">DB match</p><p className="text-body-2 text-primary fp-text flex-1 min-w-0 break-words text-right font-mono text-caption-1">{check_0.source}</p></div>;
}
function _temp4(subject, i) {
  const badge = subjectBadge(subject.checks);
  const Icon = subject.kind === "business" ? IcoStore16 : IcoUser16;
  return <div key={i} className="border border-solid border-tertiary rounded-md p-3 flex flex-col gap-4"><div className="flex items-center justify-between gap-2"><div className="flex min-w-0 items-center gap-1"><Icon className="shrink-0" color="primary" /><p className="min-w-0 break-words text-label-2 text-primary fp-text">{subject.name}</p></div><Badge variant={badge.variant}>{badge.label}</Badge></div>{(subject.role || subject.metadata) && <p className="text-body-2 text-tertiary fp-text">{[subject.role, subject.metadata].filter(Boolean).join(" \u2014 ")}</p>}{subject.checks.length > 0 && <div className="flex flex-col gap-1">{subject.checks.map(_temp)}{subject.checks.filter(_temp2).map(_temp3)}</div>}</div>;
}